import Pickr from '@simonwep/pickr';
import '@simonwep/pickr/dist/themes/nano.min.css';

const DEFAULT_LABEL = 'Aucune (couleur par défaut)';
const CHECK_ICON = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
const EYEDROPPER_ICON = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 11 3 19v2h2l8-8"/><path d="m15 5 4 4"/><path d="M17.5 2.5a2.12 2.12 0 0 1 3 3L19 7l-3-3 1.5-1.5Z"/></svg>';

const sameHex = (a, b) => !!a && !!b && a.toUpperCase() === b.toUpperCase();

// Admin product form: a visible palette (colors already used elsewhere +
// a curated set, see ProductService::suggestedBackgroundColors) for
// one-click picks, plus a Pickr popup for a fully custom color. Pickr has
// no eyedropper of its own, so the pipette button is hand-inserted into its
// popup via getRoot() and wired to the browser's native EyeDropper API —
// unsupported outside Chromium today, hence the feature check.
function initColorPickerField(root) {
    const paletteEl = root.querySelector('[data-color-picker-palette]');
    const trigger = root.querySelector('[data-color-picker-trigger]');
    const customIcon = root.querySelector('[data-color-picker-custom-icon]');
    const valueEl = root.closest('.form-group')?.querySelector('[data-color-picker-value]');
    const input = root.closest('.form-group')?.querySelector('[data-color-picker-input]');

    if (!paletteEl || !trigger || !input) return;

    let suggestedColors = [];
    try {
        suggestedColors = JSON.parse(root.dataset.suggestedColors || '[]');
    } catch {
        suggestedColors = [];
    }

    const pickr = Pickr.create({
        el: trigger,
        useAsButton: true,
        theme: 'nano',
        default: input.value || (suggestedColors[0]?.hex ?? '#E78E90'),
        swatches: suggestedColors.map((c) => c.hex),
        comparison: false,
        components: {
            preview: true,
            hue: true,
            interaction: {
                input: true,
                save: true,
            },
        },
    });

    const applyColor = (hex) => {
        input.value = hex || '';
        if (valueEl) valueEl.textContent = hex || DEFAULT_LABEL;
        updateSelection(hex);
    };

    function updateSelection(hex) {
        const paletteMatch = suggestedColors.some((c) => sameHex(c.hex, hex));

        [...paletteEl.children].forEach((btn) => {
            btn.classList.toggle('is-selected', sameHex(btn.dataset.hex, hex));
        });

        trigger.classList.toggle('is-selected', !!hex && !paletteMatch);
        trigger.style.setProperty('--custom-color', hex || 'transparent');
        if (customIcon) customIcon.innerHTML = !!hex && !paletteMatch ? CHECK_ICON : '+';
    }

    function renderPalette() {
        paletteEl.innerHTML = '';
        suggestedColors.forEach(({ hex, used }) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'color-picker-field__swatch';
            btn.style.background = hex;
            btn.dataset.hex = hex;
            btn.setAttribute('aria-label', hex);
            btn.innerHTML = CHECK_ICON;
            if (used) {
                const badge = document.createElement('span');
                badge.className = 'color-picker-field__swatch-badge';
                badge.textContent = `×${used}`;
                btn.appendChild(badge);
            }
            btn.addEventListener('click', () => {
                pickr.setColor(hex);
                applyColor(hex);
                pickr.hide();
            });
            paletteEl.appendChild(btn);
        });
    }

    pickr.on('save', (color) => {
        applyColor(color ? color.toHEXA().toString() : null);
        pickr.hide();
    });

    // Insert a pipette button into Pickr's own interaction bar, next to its
    // Save button — there's no public slot for this, so we splice it into
    // the DOM tree getRoot() exposes.
    pickr.on('init', (instance) => {
        if (!window.EyeDropper) return;

        const { save } = instance.getRoot().interaction;
        const eyedropperBtn = document.createElement('button');
        eyedropperBtn.type = 'button';
        eyedropperBtn.className = 'pcr-eyedropper';
        eyedropperBtn.setAttribute('aria-label', 'Utiliser la pipette');
        eyedropperBtn.innerHTML = EYEDROPPER_ICON;
        eyedropperBtn.addEventListener('click', async () => {
            try {
                const { sRGBHex } = await new window.EyeDropper().open();
                instance.setColor(sRGBHex);
            } catch {
                // User cancelled the eyedropper — nothing to do.
            }
        });
        save.parentElement.insertBefore(eyedropperBtn, save);
    });

    renderPalette();
    applyColor(input.value || null);
}

function initColorPickerFields() {
    document.querySelectorAll('[data-color-picker]').forEach(initColorPickerField);
}

export default initColorPickerFields;
